import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useWorkspace } from '@/api';
import { useMyPrefs, useMyProfile, useMySessions, useRevokeOtherSessions, useSaveMyPrefs, useSaveProfile } from '@/api/profiles';
import { useStudioContour } from '@/api/studio';
import { QueryPending } from '@/components/QueryState';
import { Avatar, PageHeader } from '@/components/ui';
import { formatWhen } from '@/lib/format';
import { openTour } from '@/lib/tour';
import { PrefsForm } from './Settings';

type Side = 'client' | 'studio';

const PHONE = /^[0-9+()\- ]*$/;

function Details({ side }: { side: Side }) {
  const profile = useMyProfile(side);
  const data = profile.data;
  const save = useSaveProfile(side);
  const [form, setForm] = useState({ name: '', role: '', phone: '', messenger: '' });
  const [saved, setSaved] = useState(false);
  const [problem, setProblem] = useState('');
  useEffect(() => {
    if (data) setForm({ name: data.name, role: data.role, phone: data.phone, messenger: data.messenger });
  }, [data]);
  if (!data) return <QueryPending query={profile} />;
  const set = (k: keyof typeof form, v: string) => (setSaved(false), setProblem(''), setForm({ ...form, [k]: v }));
  const field = 'block text-sm';
  const label = 'mb-1.5 block text-muted';
  return (
    <form
      className="grid grid-cols-1 gap-4 sm:grid-cols-2"
      onSubmit={(e) => {
        e.preventDefault();
        if (!form.name.trim()) return setProblem('Имя не может быть пустым');
        if (!PHONE.test(form.phone)) return setProblem('Телефон: только цифры, +, пробелы, скобки и дефис');
        save.mutate(form, { onSuccess: () => setSaved(true), onError: (err) => setProblem(err.message) });
      }}
    >
      <label className={field}>
        <span className={label}>Имя</span>
        <input className="field" name="name" autoComplete="name" maxLength={200} value={form.name} onChange={(e) => set('name', e.target.value)} />
      </label>
      <label className={field}>
        <span className={label}>Должность</span>
        <input className="field" name="role" maxLength={200} value={form.role} onChange={(e) => set('role', e.target.value)} />
      </label>
      <label className={field}>
        <span className={label}>Телефон</span>
        <input className="field" name="phone" type="tel" autoComplete="tel" maxLength={40} placeholder="+7 (999) 123-45-67" value={form.phone} onChange={(e) => set('phone', e.target.value)} />
      </label>
      <label className={field}>
        <span className={label}>Мессенджер</span>
        <input className="field" name="messenger" maxLength={120} placeholder="@handle или Telegram: …" value={form.messenger} onChange={(e) => set('messenger', e.target.value)} />
      </label>
      <div className={`${field} sm:col-span-2`}>
        <label htmlFor="profile-email" className={label}>
          Почта для входа
        </label>
        <input id="profile-email" className="field" name="email" value={data.email} readOnly aria-describedby="profile-email-note" />
        <span id="profile-email-note" className="mt-1.5 block text-xs text-muted">
          Почту для входа меняет владелец кабинета.
        </span>
      </div>
      <div className="flex flex-wrap items-center gap-3 sm:col-span-2">
        <button className="btn-primary" disabled={save.isPending}>
          Сохранить
        </button>
        {saved && (
          <span role="status" className="text-sm text-done">
            Сохранено.
          </span>
        )}
        {problem && (
          <span role="alert" className="text-sm text-overdue">
            {problem}
          </span>
        )}
      </div>
    </form>
  );
}

function Prefs({ telegram, acts, studio }: { telegram: boolean; acts: boolean; studio: boolean }) {
  const prefs = useMyPrefs();
  const data = prefs.data;
  const save = useSaveMyPrefs();
  if (!data) return <QueryPending query={prefs} />;
  return <PrefsForm data={data} onChange={(p) => save.mutate(p)} telegram={telegram} acts={acts} studio={studio} />;
}

function ClientPrefs() {
  const { data: ws } = useWorkspace();
  return <Prefs studio={false} telegram={!!ws?.contour.channels.includes('telegram')} acts={!!ws?.contour.legal.acts} />;
}

function StudioPrefs() {
  const { data: contour } = useStudioContour();
  return <Prefs studio telegram={!!contour?.channels.includes('telegram')} acts={false} />;
}

function Sessions() {
  const sessions = useMySessions();
  const data = sessions.data;
  const revoke = useRevokeOtherSessions();
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState('');
  if (!data) return <QueryPending query={sessions} />;
  const others = data.filter((s) => !s.current).length;
  return (
    <>
      <ul className="divide-y divide-line border-y border-line text-sm" aria-label="Мои сессии">
        {data.map((s) => (
          <li key={s.createdAt} className="flex flex-wrap items-baseline gap-x-3 py-2.5">
            <span className="font-medium">{s.current ? 'Это устройство' : 'Другое устройство'}</span>
            <span className="text-xs text-muted">вошли {formatWhen(s.createdAt)}</span>
            <span className="text-xs text-muted">последний раз {formatWhen(s.lastSeenAt)}</span>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <button
          className="btn-secondary"
          disabled={others === 0 || revoke.isPending}
          onClick={() =>
            revoke.mutate(undefined, {
              onSuccess: (r) => (setProblem(''), setNote(r.ended ? 'Вы вышли на других устройствах.' : 'Других устройств нет.')),
              onError: (e) => (setNote(''), setProblem(e.message)),
            })
          }
        >
          Выйти на других устройствах
        </button>
        {others === 0 && !note && <span className="text-xs text-muted">Вы вошли только здесь.</span>}
        {note && (
          <span role="status" className="text-sm text-done">
            {note}
          </span>
        )}
        {problem && (
          <span role="alert" className="text-sm text-overdue">
            {problem}
          </span>
        )}
      </div>
    </>
  );
}

/** «Мой профиль»: the same page for a client's person and a studio member; the BFF knows who is asking. */
export function Profile({ side }: { side: Side }) {
  const me = useMyProfile(side).data;
  return (
    <>
      <PageHeader
        title={
          <span className="flex items-center gap-3">
            {me && <Avatar person={me} />}
            Мой профиль
          </span>
        }
        description="Как вас видят коллеги и клиенты: имя, должность и контакты. Телефон и мессенджер показываются тем, кто может открыть вашу карточку."
      />
      <div className="max-w-3xl space-y-12">
        <section>
          <h2 className="mb-4 text-sm font-semibold">Данные</h2>
          <Details side={side} />
        </section>
        <section>
          <h2 className="mb-4 text-sm font-semibold">Мои уведомления</h2>
          {side === 'client' ? <ClientPrefs /> : <StudioPrefs />}
        </section>
        <section>
          <h2 className="mb-4 text-sm font-semibold">Мои сессии</h2>
          <Sessions />
        </section>
        {side === 'client' && (
          <section>
            <h2 className="mb-2 text-sm font-semibold">Подсказки</h2>
            <button type="button" className="btn-secondary" onClick={openTour}>
              Как пользоваться кабинетом
            </button>
          </section>
        )}
        {side === 'client' && (
          <p className="text-sm text-muted">
            Реквизиты и люди компании — в разделе{' '}
            <Link to="/settings" className="underline underline-offset-2 hover:text-ink">
              «Компания и команда»
            </Link>
            .
          </p>
        )}
      </div>
    </>
  );
}
